import React, { useState, useCallback, useEffect } from 'react';
import {
  View,
  TouchableOpacity,
  StyleSheet,
} from 'react-native';
import { Text } from '@/components/common/text';
import { useMarketplaceColors } from '@/constants/residents/marketplaceColors';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { PaymentOptionButton, PaymentCheckbox } from './shared';
import type { PaymentMethod, FeeStructure } from '@/types/residents/payment';

interface ChangePaymentMethodContentProps {
  paymentMethods: PaymentMethod[];
  selectedMethodId: number | null;
  onMethodSelected: (methodId: number) => void;
  onAddCard: () => void;
  onAddACH: () => void;
  onManageWallet: () => void;
  feeStructure: FeeStructure;
}

/**
 * Change Payment Method Content - Shows saved payment methods
 */
export const ChangePaymentMethodContent: React.FC<ChangePaymentMethodContentProps> = ({
  paymentMethods,
  selectedMethodId,
  onMethodSelected,
  onAddCard,
  onAddACH,
  onManageWallet,
  feeStructure,
}) => {
  const MARKETPLACE_COLORS = useMarketplaceColors();
  const { colors } = useThemeColors();
  const [tempSelectedId, setTempSelectedId] = useState<number | null>(selectedMethodId);

  // Reset temp selection when component mounts
  useEffect(() => {
    setTempSelectedId(selectedMethodId);
  }, [selectedMethodId]);

  const handleSelect = useCallback((sourceId: number) => {
    setTempSelectedId(sourceId);
    // Immediately notify parent of selection
    onMethodSelected(sourceId);
  }, [onMethodSelected]);

  const getMethodLabel = (method: PaymentMethod): string => {
    const brand = method.brand || (method.type === 'card' ? 'Card' : 'ACH');
    return `${brand} *${method.last4}`;
  };

  const allMethodsExpired = paymentMethods.length > 0 && 
    paymentMethods.every((m) => m.isExpired);

  return (
    <View style={styles.container}>
      <View style={styles.content}>
        {/* Instructions */}
        <Text style={[styles.instructionText, { color: MARKETPLACE_COLORS.textPrimary }]}>
          Please choose the type of payment you wish to use
        </Text>

        {/* Add New Options */}
        <View style={styles.optionsContainer}>
          <PaymentOptionButton
            label="Credit or debit card"
            onPress={onAddCard}
          />
          <PaymentOptionButton
            label="ACH bank transfer"
            onPress={onAddACH}
          />
        </View>

        {/* Saved Payment Methods Section */}
        {paymentMethods.length > 0 && (
          <View style={styles.savedMethodsSection}>
            {/* Header with Manage link */}
            <View style={styles.savedMethodsHeader}>
              <Text style={[styles.savedMethodsTitle, { color: MARKETPLACE_COLORS.textPrimary }]}>Saved payment methods</Text>
              <TouchableOpacity onPress={onManageWallet} activeOpacity={0.7}>
                <Text style={[styles.manageLink, { color: MARKETPLACE_COLORS.accentBlueSolid }]}>Manage</Text>
              </TouchableOpacity>
            </View>

            {/* All expired warning */}
            {allMethodsExpired && (
              <Text style={[styles.expiredWarning, { color: MARKETPLACE_COLORS.accentRed }]}>
                All saved payment methods have expired. Please add a new payment method.
              </Text>
            )}

            {/* Saved methods list */}
            <View style={styles.savedMethodsList}>
              {paymentMethods.map((method) => {
                const sourceId = Number(method.sourceId) || Number(method.id);
                const isSelected = tempSelectedId === sourceId;
                const isExpired = method.isExpired;

                return (
                  <TouchableOpacity
                    key={method.id}
                    style={[
                      styles.savedMethodItem,
                      { borderColor: MARKETPLACE_COLORS.textPrimary },
                      isSelected && !isExpired && { borderColor: MARKETPLACE_COLORS.accentBlueSolid },
                      isExpired && [styles.savedMethodItemExpired, { borderColor: MARKETPLACE_COLORS.inputBorder }],
                    ]}
                    onPress={() => !isExpired && handleSelect(sourceId)}
                    activeOpacity={isExpired ? 1 : 0.8}
                    disabled={isExpired}
                  >
                    <View style={styles.savedMethodLeft}>
                      {/* Checkbox */}
                      <PaymentCheckbox
                        checked={isSelected && !isExpired}
                        onPress={() => !isExpired && handleSelect(sourceId)}
                        label=""
                        disabled={isExpired}
                      />
                    </View>

                    <View style={styles.savedMethodContent}>
                      <Text style={[
                        styles.savedMethodLabel,
                        { color: MARKETPLACE_COLORS.textPrimary },
                        isExpired && { color: MARKETPLACE_COLORS.textMuted },
                      ]} numberOfLines={2}>
                        {getMethodLabel(method)}
                      </Text>
                      {method.isDefault && !isExpired && (
                        <Text style={[styles.defaultLabel, { color: colors.accent.green }]}>Default</Text>
                      )}
                      {isExpired && (
                        <Text style={[styles.expiredLabel, { color: MARKETPLACE_COLORS.dangerRed }]}>Expired</Text>
                      )}
                    </View>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    paddingHorizontal: 24,
    paddingVertical: 24,
    gap: 16,
  },
  instructionText: {
    fontSize: 16,
    fontWeight: '400',
    lineHeight: 24,
  },
  optionsContainer: {
    gap: 16,
  },
  savedMethodsSection: {
    marginTop: 16,
  },
  savedMethodsHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  savedMethodsTitle: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  manageLink: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  expiredWarning: {
    fontSize: 12,
    fontWeight: '400',
    marginBottom: 12,
  },
  savedMethodsList: {
    gap: 16,
  },
  savedMethodItem: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 40,
    paddingVertical: 7, // 40 − 24 checkbox slot − 2×1 border, split (the slot is taller than the 21pt line)
    paddingHorizontal: 16,
    borderWidth: 1,
    borderRadius: 8,
  },
  savedMethodItemExpired: {
    opacity: 0.7,
  },
  savedMethodLeft: {
    width: 28,
    height: 24,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  savedMethodContent: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  savedMethodLabel: {
    flexShrink: 1,
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  defaultLabel: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
  expiredLabel: {
    fontSize: 14,
    fontWeight: '500',
    lineHeight: 21,
  },
});

export default ChangePaymentMethodContent;
